/**
 * Load More Button Web Component for Collection Pages
 * Uses Shopify Section Rendering API to load next page products
 */
class LoadMoreButton extends HTMLElement {
  constructor() {
    super();

    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleFilterUpdate = this.handleFilterUpdate.bind(this);

    // Component state
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.sectionId = this.dataset.sectionId;
    this.baseUrl = this.dataset.baseUrl;
    this.totalItems = parseInt(this.dataset.totalItems, 10) || 0;
    this.itemsShown = parseInt(this.dataset.itemsShown, 10) || 0;
    this.productsPerPage = parseInt(this.dataset.productsPerPage, 10) || 12;
    this.loadMoreTextTemplate = this.dataset.loadMoreTextTemplate;
    this.isLoading = false;
  }

  connectedCallback() {
    this.button = this.querySelector('[data-load-more-button]');
    this.spinner = this.querySelector('[data-load-more-spinner]');
    this.content = this.querySelector('[data-load-more-content]');
    this.countText = this.querySelector('[data-count-shown]');
    this.progressBar = this.querySelector('[data-progress-bar]');

    // Try to restore state
    if (!this.restoreState()) {
      // Initial state if restore failed/not available
      this.updateButtonState();
      this.updateProgress();
    }

    // Event listeners
    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }

    // Listen for filter updates
    document.addEventListener('theme:filter:update', this.handleFilterUpdate);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }

    document.removeEventListener(
      'theme:filter:update',
      this.handleFilterUpdate
    );
  }

  /**
   * Handle button click to load more products
   */
  async handleClick() {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.setLoading(true);

    try {
      const nextPage = this.currentPage + 1;
      const url = this.buildSectionUrl(nextPage);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Update current page before appending so saveState captures the correct page
      this.currentPage = nextPage;

      // Direct HTML response - append products
      this.appendProducts(html);

      this.updateButtonState();

      // Reflect page param in browser URL
      this.updateBrowserUrl(nextPage);

      // Dispatch event for other components
      this.dispatchLoadMoreEvent(nextPage);
    } catch (error) {
      console.error('Load More Error:', error);
      this.showError();
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Handle filter/sort updates to reset load more state
   */
  handleFilterUpdate(event) {
    // Clear storage on filter update
    this.clearState();

    // Reset to page 1 when filters are applied
    this.currentPage = 1;

    // Get new pagination info from updated page
    this.updatePaginationInfo();
  }

  /**
   * Build URL for Shopify Section Rendering API
   */
  buildSectionUrl(page) {
    const url = new URL(window.location.pathname, window.location.origin);

    // Add page parameter
    url.searchParams.set('page', page);

    // Add section rendering parameter - use singular 'section_id' for clean HTML response
    url.searchParams.set('section_id', this.sectionId);

    // Preserve existing filter and sort parameters
    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of currentParams) {
      if (key !== 'page' && key !== 'section_id') {
        url.searchParams.set(key, value);
      }
    }

    return url.toString();
  }

  /**
   * Update the browser URL to reflect current page and params
   */
  updateBrowserUrl(page) {
    const url = new URL(window.location.href);

    // Update page parameter
    url.searchParams.set('page', page);

    // Preserve other params automatically (URL constructed from location.href)
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Append new products to the grid
   */
  appendProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find the product grid container in the response (with data-product-grid)
    const responseProductGrid = doc.querySelector('[data-product-grid]');
    // Find the product grid container in the current page
    const currentProductGrid = document.querySelector('[data-product-grid]');

    if (responseProductGrid && currentProductGrid) {
      // Get all product grid items from the response
      const newProducts = responseProductGrid.querySelectorAll(
        '[data-product-grid-item]'
      );

      newProducts.forEach((product) => {
        // Clone the element to avoid DOMParser issues
        const clonedProduct = product.cloneNode(true);

        // Re-initialize AOS animations for new products
        const aosElements = clonedProduct.querySelectorAll('[data-aos]');
        aosElements.forEach((el) => {
          el.setAttribute('data-aos-processed', 'false');
        });

        currentProductGrid.appendChild(clonedProduct);
      });

      this.itemsShown += newProducts.length;
      this.updateProgress();

      // Save state after update
      this.saveState();

      // Re-initialize AOS
      if (window.AOS) {
        window.AOS.refreshHard();
      }
    } else {
      // Could not find product grid containers
    }
  }

  /**
   * Update pagination info from current page
   */
  updatePaginationInfo() {
    const pagination = document.querySelector('[data-pagination-info]');
    if (pagination) {
      this.totalPages = parseInt(pagination.dataset.totalPages, 10) || 1;
      this.totalItems =
        parseInt(pagination.dataset.totalItems, 10) || this.totalItems;
      this.itemsShown =
        parseInt(pagination.dataset.itemsShown, 10) || this.itemsShown;
      this.updateProgress();
    }
    this.updateButtonState();
  }

  /**
   * Get unique storage key for current collection/search context
   */
  getStorageKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return `load-more-${url.pathname}-${url.search}`;
  }

  /**
   * Save current state to session storage
   */
  saveState() {
    if (!window.LZString) return;
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;

    const state = {
      html: window.LZString.compressToUTF16(grid.innerHTML),
      currentPage: this.currentPage,
      itemsShown: this.itemsShown,
      totalItems: this.totalItems,
      timestamp: Date.now(),
    };

    try {
      sessionStorage.setItem(this.getStorageKey(), JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save load more state', e);
    }
  }

  /**
   * Restore state from session storage
   */
  restoreState() {
    if (!window.LZString) return false;

    try {
      const stored = sessionStorage.getItem(this.getStorageKey());
      if (!stored) return false;

      const state = JSON.parse(stored);

      // Basic expiration/validation (optional, but good practice)
      // For now we trust the key matching

      const html = window.LZString.decompressFromUTF16(state.html);
      if (!html) return false;

      const grid = document.querySelector('[data-product-grid]');
      if (!grid) return false;

      grid.innerHTML = html;
      this.currentPage = state.currentPage;
      this.itemsShown = state.itemsShown;
      this.totalItems = state.totalItems;

      this.updateProgress();
      this.updateButtonState();

      // Re-initialize AOS
      if (window.AOS) {
        // Short timeout to ensure DOM is ready
        setTimeout(() => window.AOS.refreshHard(), 100);
      }

      return true;
    } catch (e) {
      console.error('Failed to restore state', e);
      return false;
    }
  }

  /**
   * Clear saved state
   */
  clearState() {
    try {
      sessionStorage.removeItem(this.getStorageKey());
    } catch (e) {
      // Ignore errors
    }
  }

  /**
   * Update progress bar and text
   */
  updateProgress() {
    if (this.countText) {
      this.countText.textContent = this.itemsShown;
    }
    if (this.progressBar && this.totalItems > 0) {
      const percentage = Math.min(
        (this.itemsShown / this.totalItems) * 100,
        100
      );
      this.progressBar.style.width = `${percentage}%`;
    }

    if (this.content && this.loadMoreTextTemplate) {
      const remaining = this.totalItems - this.itemsShown;
      const nextCount = Math.min(remaining, this.productsPerPage);
      // Replace [count] with the actual number
      this.content.textContent = this.loadMoreTextTemplate.replace(
        '[count]',
        nextCount
      );
    }
  }

  /**
   * Update button visibility and text
   */
  updateButtonState() {
    if (!this.button) return;

    let hasMorePages = this.currentPage < this.totalPages;

    // Extra check: if we have shown all items, hide button even if page count is desynced
    if (this.totalItems > 0 && this.itemsShown >= this.totalItems) {
      hasMorePages = false;
    }

    if (hasMorePages) {
      this.classList.remove('hidden');
      this.button.disabled = false;
    } else {
      this.classList.add('hidden');
    }
  }

  /**
   * Set loading state
   */
  setLoading(loading) {
    this.isLoading = loading;

    if (this.button) {
      this.button.disabled = loading;
    }

    if (this.spinner) {
      this.spinner.classList.toggle('hidden', !loading);
    }

    if (this.content) {
      this.content.classList.toggle('hidden', loading);
    }
  }

  /**
   * Show error message
   */
  showError() {
    if (this.button) {
      const originalText = this.content
        ? this.content.textContent
        : 'Load more';
      this.content.textContent = 'Error loading products. Please try again.';

      setTimeout(() => {
        this.content.textContent = originalText;
      }, 3000);
    }
  }

  /**
   * Dispatch custom event after loading products
   */
  dispatchLoadMoreEvent(page) {
    const event = new CustomEvent('theme:load-more:loaded', {
      detail: {
        page: page,
        totalPages: this.totalPages,
        baseUrl: this.baseUrl,
      },
      bubbles: true,
    });

    this.dispatchEvent(event);
  }
}

// Register the custom element
customElements.define('load-more-button', LoadMoreButton);

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LoadMoreButton;
}
